import { Accordion, AccordionDetails, AccordionSummary, Box, Dialog, IconButton, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface SlidesPageProps {
  interactionId: string;
  slidesData: any;
}

interface SlideFrame {
  frame_number: number;
  start_timestamp: number;
  end_timestamp: number;
  s3_url: string;
  title?: string;
  kpi: string;
  duration: number;
  summary: string;
}

const SlidesPage = ({ slidesData }: SlidesPageProps) => {
  const [filteredFrames, setFilteredFrames] = useState<SlideFrame[]>([]);
  const [expandedIndex, setExpandedIndex] = useState<number | false>(false);
  const [fullscreenSlide, setFullscreenSlide] = useState<SlideFrame | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (expandedIndex !== false && slideRefs.current[expandedIndex]) {
      slideRefs.current[expandedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }, [expandedIndex]);

  const preloadedImages = useRef<Map<number, HTMLImageElement>>(new Map());

  const preloadAroundSlide = (slideIndex: number, frames: SlideFrame[]) => {
    const start = Math.max(0, slideIndex - 5);
    const end = Math.min(frames.length - 1, slideIndex + 5);

    for (let i = start; i <= end; i++) {
      if (!preloadedImages.current.has(i)) {
        const img = new Image();
        img.src = frames[i].s3_url;
        preloadedImages.current.set(i, img);
      }
    }

    for (const [index] of preloadedImages.current) {
      if (index < start || index > end) {
        preloadedImages.current.delete(index);
      }
    }
  };

  const formatKpi = (kpi: string) => {
    switch (kpi) {
      case "business_kpi":
        return "Business";
      case "operational_kpi":
        return "Operational";
      case "none":
      default:
        return "";
    }
  };

  useEffect(() => {
    if (slidesData) {
      const allFrames: SlideFrame[] = slidesData?.data?.frames || [];
      const validFrames = allFrames.filter(
        (frame) => frame.s3_url && frame.s3_url.trim() !== ""
      );

      const enriched = validFrames.map((frame, idx) => ({
        ...frame,
        title: frame.title ? `Slide ${idx + 1} : ${frame.title}` : `Slide ${idx + 1}`,
        start_timestamp: frame.start_timestamp || 0,
        end_timestamp: frame.end_timestamp || 0,
        duration: frame.duration ?? null,
        summary: frame.summary || "No summary available"
      }));

      setFilteredFrames(enriched);
    }
  }, [slidesData]);

  const formatMinutes = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds} Seconds`;
    }
    const mins = Math.floor(seconds / 60);
    return `${mins} Minutes`;
  };

  return (
    <>
      {filteredFrames.length === 0 ? (
        <Box className="slides__empty-container">
          <Typography className="slides__empty-message">
            No slides to view.
          </Typography>
        </Box>
      ) : (
      filteredFrames.map((slide, index) => {
        const isExpanded = expandedIndex === index;
        return (
          <Box key={index} sx={{ position: "relative", width: "100%" }}>
            {isExpanded && (
              <Box
                sx={{
                  position: "absolute",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  top: 8,
                  right: 8,
                  padding: "12px",
                  zIndex: 10,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Previous" placement="top" arrow>
                <span>
                <IconButton
                  disabled={index === 0}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    const prevIndex = index - 1;
                    setExpandedIndex(prevIndex);
                    setTimeout(() => {
                      slideRefs.current[prevIndex]?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    }, 100);
                  }}
                >
                  <ExpandLessIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Box className="index-box"> 
              <Typography className="index-text"> 
                {index + 1} of {filteredFrames.length} 
              </Typography> 
            </Box>

            <Tooltip title="Next" placement="bottom" arrow>
              <span>
                <IconButton
                  disabled={index === filteredFrames.length - 1}
                  size="small"
                  onClick={(e) => {
                  e.stopPropagation();
                  const nextIndex = index + 1;
                  setExpandedIndex(nextIndex);
                  setTimeout(() => {
                    slideRefs.current[nextIndex]?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  }, 100);
                }}
                >
                  <ExpandMoreIcon />
                </IconButton>
                </span>
                </Tooltip>
              </Box>
            )}
          <Accordion
            key={index}
            expanded={isExpanded}
            ref={(el) => (slideRefs.current[index] = el)}
            onChange={(_e, expanded) => {
              setExpandedIndex(expanded ? index : false);
              if (expanded) preloadAroundSlide(index, filteredFrames);
            }}
            TransitionProps={{
              onEntered: () => {
                slideRefs.current[index]?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              },
            }}
            disableGutters
            sx={{
              border: "none",
              borderBottom: "1px solid #ddd",              
              boxShadow: "none",
              padding: "0px 0px",
              paddingRight: "8px",
            }}
          >
            <AccordionSummary
              expandIcon={!isExpanded ? <ExpandMoreIcon /> : null}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                minHeight: "auto",
                padding: "4px 4px",
                marginLeft: "12px",
                "& .MuiAccordionSummary-expandIconWrapper": {
                  transform: "none !important",
                  paddingTop: "20px",
                },
              }}
            >
              {!isExpanded && slide?.s3_url && (
                <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                  <img
                    src={slide.s3_url}
                    alt="Slide Preview"
                    style={{
                      maxHeight: "50px",
                      maxWidth: "80px",
                      objectFit: "contain",
                      borderRadius: "4px"
                    }}
                  />
                </Box>
              )}
              <Typography variant="h3" sx={{ mr: 1, alignItems: "center",mt: 2  }}>
                {slide.title}:
              </Typography>
              <Typography variant="h3" sx={{ color: "#9595A3", alignItems: "center", mt: 2, fontWeight: 200}}>
                Start time: {formatMinutes(slide.start_timestamp)}
                {slide.duration ? ` | Duration: ${formatMinutes(slide.duration)}` : ""}
                {formatKpi(slide.kpi) ? ` | Type: ${formatKpi(slide.kpi)}`  : ""}
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              {isExpanded && slide?.s3_url && (
                <Box sx={{ display: "flex", alignItems: "center", flexDirection:"column", gap: 2, flex: 1, width: "100%" }}>
                  <img
                    src={slide.s3_url}
                    alt="Slide Preview"
                    style={{
                      maxHeight: "340px",
                      maxWidth: "600px",
                      objectFit: "contain"
                    }}
                    onClick={() => setFullscreenSlide(slide)}
                  />
                  <Typography className="slides-summary">
                    <span>Summary: </span>
                    <span className="text">{slide.summary}</span>
                  </Typography>
                </Box>
              )}
          </AccordionDetails>
        </Accordion>
        </Box>
      );
      }))}
      <Dialog
        open={!!fullscreenSlide}
        onClose={() => setFullscreenSlide(null)}
        fullScreen
        PaperProps={{
          sx: { backgroundColor: "black" }
        }}
      >
        <IconButton
          onClick={() => setFullscreenSlide(null)}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "white",
            zIndex: 10
          }}
        >
          <CloseIcon />
        </IconButton>
        {fullscreenSlide && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <img
              src={fullscreenSlide.s3_url}
              alt="Fullscreen Slide"
              style={{
                maxHeight: "100%",
                maxWidth: "100%",
                objectFit: "contain"
              }}
            />
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default SlidesPage;
